import { z } from "zod"
import {
  contactTypeEnum,
  estateRoleEnum,
  insuranceRoleEnum,
  maritalStatusEnum,
  relationshipTypeEnum,
  rolePositionEnum,
} from "../../db/schema/contact"
import { zJournal } from "../../routes/api/journal/types"

export const legacyBeneficiaryTypeValues = [
  "UNGROUPED",
  "RESIDUAL",
  "INSURANCE",
  "TRUST",
  "NAMED_ON_ACCOUNT",
  "SPECIAL_BEQUEST",
] as const

export const zLegacyBeneficiaryType = z.enum(legacyBeneficiaryTypeValues)

export const zListContactInput = z.object({
  estateId: z.string().uuid(),
  contactType: z.enum(contactTypeEnum.enumValues).optional(),
  estateRoles: z.array(z.enum(estateRoleEnum.enumValues)).optional(),
})

export const zListEstateContactsInput = zListContactInput.extend({
  userId: z.string().uuid(),
})

export type ListEstateContactsInput = z.infer<typeof zListEstateContactsInput>

export const zEstateContactRole = z.object({
  id: z.string().uuid(),
  estateId: z.string().uuid(),
  contactId: z.string().uuid(),
  estateRole: z.enum(estateRoleEnum.enumValues).nullable(),
  rolePosition: z.enum(rolePositionEnum.enumValues).nullable(),
  profilePictureUrl: z.string().nullable(),
  estateInstructions: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  notes: z.string().nullable(),
  isDraft: z.boolean(),
})

export const zListEstateContactsOutput = z.array(
  z.object({
    id: z.string().uuid(),
    contactType: z.enum(contactTypeEnum.enumValues).nullable(),
    maritalStatus: z.enum(maritalStatusEnum.enumValues).nullable(),
    estateId: z.string().uuid().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    dateOfDeath: z.string().nullable(),
    maritalStatusDate: z.string().nullable(),
    relationshipStartDate: z.string().nullable(),
    relationshipEndDate: z.string().nullable(),
    locationId: z.string().uuid().nullable(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    beneficiaryType: zLegacyBeneficiaryType.nullable(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    beneficiaryTypes: z.array(zLegacyBeneficiaryType).nullable(),
    /** @deprecated Remove after frontend migrates to beneficiary tRPC procedures (#689) */
    residualPercentage: z.number().int().nullable(),
    insuranceRole: z.enum(insuranceRoleEnum.enumValues).nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    journals: z.array(zJournal).nullish(),
    estateRole: z.enum(estateRoleEnum.enumValues).nullish(),
    rolePosition: z.enum(rolePositionEnum.enumValues).nullish(),
    estateRoles: z.array(zEstateContactRole),
    parentContactId: z.string().uuid().nullable(),
    relationshipType: z.enum(relationshipTypeEnum.enumValues).nullable(),
    parentRelationships: z
      .array(
        z.object({
          relationshipId: z.string().uuid(),
          parentContactId: z.string().uuid(),
          relationshipType: z.enum(relationshipTypeEnum.enumValues),
        }),
      )
      .nullable(),
  }),
)

export type ListEstateContactsOutput = z.infer<typeof zListEstateContactsOutput>

// --- listBeneficiariesByType ---

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export const zListBeneficiariesByTypeInput = z.object({
  estateId: z.string().uuid(),
  beneficiaryTypes: z.array(zLegacyBeneficiaryType).min(1),
})

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export type ListBeneficiariesByTypeInputType = z.infer<typeof zListBeneficiariesByTypeInput> & {
  userId: string
}

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export const zListBeneficiariesByTypeOutput = z.array(
  z.object({
    id: z.string().uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    contactType: z.enum(contactTypeEnum.enumValues).nullable(),
    beneficiaryTypes: z.array(zLegacyBeneficiaryType).nullable(),
    location: z
      .object({
        city: z.string().nullable(),
        state: z.string().nullable(),
        country: z.string().nullable(),
      })
      .nullable(),
  }),
)

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export type ListBeneficiariesByTypeOutputType = z.infer<typeof zListBeneficiariesByTypeOutput>

// --- listResidualBeneficiaries ---

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export const zListResidualBeneficiariesInput = z.object({
  estateId: z.string().uuid(),
})

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export type ListResidualBeneficiariesInputType = z.infer<typeof zListResidualBeneficiariesInput> & {
  userId: string
}

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export const zListResidualBeneficiariesOutput = z.array(
  z.object({
    id: z.string().uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    contactType: z.enum(contactTypeEnum.enumValues).nullable(),
    beneficiaryTypes: z.array(zLegacyBeneficiaryType).nullable(),
    residualPercentage: z.number().int().nullable(),
    location: z
      .object({
        city: z.string().nullable(),
        state: z.string().nullable(),
        country: z.string().nullable(),
      })
      .nullable(),
  }),
)

/** @deprecated Will be replaced by beneficiary module equivalents. See #689. */
export type ListResidualBeneficiariesOutputType = z.infer<typeof zListResidualBeneficiariesOutput>

export const zParentRelationship = z.object({
  id: z.string().uuid(),
  parentContactId: z.string().uuid(),
  relationshipType: z.enum(relationshipTypeEnum.enumValues),
})

export type ParentRelationship = z.infer<typeof zParentRelationship>
